import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import ModalMAIQ from 'app/components/ModalMAIQ';
import { useSectionL2Modal } from '../hooks/useSectionL2Modal';
import type { Section } from '../types';
import Metric from './Metric';
import Insight from './Insight';

const gridColumns = (layout: Section['layout']): number => {
  switch (layout) {
    case 'grid-1':
      return 12;
    case 'grid-2':
      return 6;
    case 'grid-3':
      return 4;
    case 'grid-4':
      return 3;
    default:
      return 12;
  }
};

const spanToMd = (span: Section['items'][number]['span'], fallback: number): number => {
  if (span === 'full') return 12;
  if (typeof span === 'number') return Math.min(12, span * 3);
  return fallback;
};

/** Section-level L2 modal — re-renders section items with full_records (dataLevel L2). */
export default function SectionL2Modal() {
  const { open, section, title, overline, onClose } = useSectionL2Modal();
  const col = section ? gridColumns(section.layout) : 12;

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton>
      <Paper
        elevation={0}
        sx={{
          width: 960,
          maxWidth: '94vw',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        <Box className="modal-header" sx={{ p: 2, zIndex: 10, flexShrink: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box>
              {overline ? (
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.85, letterSpacing: '0.06em', display: 'block' }}
                >
                  {overline}
                </Typography>
              ) : null}
              <Typography variant="h6" fontWeight={600}>
                {title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Detailed view
              </Typography>
            </Box>
            <Tooltip title="Close">
              <IconButton
                className="modal-header-button"
                aria-label="close"
                onClick={onClose}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ px: 0, pt: 0, pb: 2, overflow: 'auto', flex: 1, minHeight: 0 }}>
          {section ? (
            <Grid container spacing={0} alignItems="stretch">
              {section.items.map(item => (
                <Grid
                  item
                  xs={12}
                  sm={section.layout === 'grid-1' ? 12 : 6}
                  md={spanToMd(item.span, col)}
                  key={`${item.kind}-${item.id}`}
                  sx={{ display: 'flex' }}
                >
                  <Box sx={{ width: '100%', display: 'flex' }}>
                    {item.kind === 'metric' ? (
                      <Metric
                        id={item.id}
                        span={item.span}
                        renderer={item.renderer}
                        presentation={item.presentation}
                        dataLevel="L2"
                      />
                    ) : (
                      <Insight id={item.id} />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : null}
        </Box>
      </Paper>
    </ModalMAIQ>
  );
}
